import { col, type Shape } from './base.ts';

export const WELLTEST_INDEX_PRESSURE: Shape = [
  col('injectivityIndex', 'productivityIndex', 'Well injectivity index.', { suffix: 'InjectivityIndex' }),
  col('injectivityIndexPercentChange', 'fraction', 'Percent change in well injectivity index.', {
    suffix: 'InjectivityIndexPercentChange',
  }),
  col('productivityIndex', 'productivityIndex', 'Well productivity index.', { suffix: 'ProductivityIndex' }),
  col('productivityIndexPercentChange', 'fraction', 'Percent change in well productivity index.', {
    suffix: 'ProductivityIndexPercentChange',
  }),
  col('reservoirPressure', 'pressureAbsolute', 'Estimated reservoir pressure.', { suffix: 'ReservoirPressure' }),
  col('reservoirPressurePercentChange', 'fraction', 'Percent change in estimated reservoir pressure.', {
    suffix: 'ReservoirPressurePercentChange',
  }),
];

/**
 * The model-estimated phase rates + flowing bottom-hole pressure on
 * `WellTestAnalysis_Agent` (B3), applied with `prefix:'modelEstimated'` to
 * de-mark the `modelEstimated` marker (`modelEstimatedOilRate` → `estimatedOilRate`,
 * …). Distinct from {@link PHASE_RATES_STD} / {@link METHOD_ESTIMATE} (those carry
 * a `Std` tail or a different prefix); these are the well-test model's estimated
 * flowing-condition rates. The canonicals keep the `estimated` family marker so
 * they stay distinct from the last-well-test measured snapshot
 * ({@link WELLTEST_LAST_TEST}) composed alongside them in the same view.
 */
export const WELLTEST_ESTIMATED_RATES: Shape = [
  col('estimatedFbhp', 'pressureAbsolute', 'Model-estimated flowing bottom-hole pressure.', { suffix: 'Fbhp' }),
  col('estimatedGasRate', 'gasRate', 'Model-estimated gas rate.', { suffix: 'GasRate' }),
  col('estimatedLiquidRate', 'liquidRate', 'Model-estimated liquid rate.', { suffix: 'LiquidRate' }),
  col('estimatedOilRate', 'liquidRate', 'Model-estimated oil rate.', { suffix: 'OilRate' }),
  col('estimatedWaterRate', 'liquidRate', 'Model-estimated water rate.', { suffix: 'WaterRate' }),
];

/**
 * The last-well-test measured snapshot on `WellTestAnalysis_Agent` (B3), applied
 * with `prefix:'modelWellTest'` to de-mark the `modelWellTest` marker
 * (`modelWellTestGasRate` → `lastTestGasRate`, …): casing-head pressure, gas rate,
 * GOR, liquid rate, PDG pressure, timestamp, water cut, well-head pressure. The
 * canonicals keep the `lastTest` family marker so the measured snapshot stays
 * distinct from the model-estimated rates ({@link WELLTEST_ESTIMATED_RATES})
 * composed alongside it in the same view.
 */
export const WELLTEST_LAST_TEST: Shape = [
  col('lastTestChp', 'pressureAbsolute', 'Last well-test casing-head pressure.', { suffix: 'Chp' }),
  col('lastTestGasRate', 'gasRate', 'Last well-test gas rate.', { suffix: 'GasRate' }),
  col('lastTestGor', 'gasOilRatio', 'Last well-test gas-oil ratio.', { suffix: 'Gor' }),
  col('lastTestLiquidRate', 'liquidRate', 'Last well-test liquid rate.', { suffix: 'LiquidRate' }),
  col('lastTestPdgPressure', 'pressureAbsolute', 'Last well-test permanent-downhole-gauge pressure.', {
    suffix: 'PdgPress',
  }),
  col('lastTestTimestamp', 'unitless', 'Last well-test timestamp.', { suffix: 'Timestamp' }),
  col(
    'lastTestWc',
    'fraction',
    'Last well-test water cut — explicit well-test asks only; default water cut is derived from allocated rates.',
    { suffix: 'Wc' },
  ),
  col('lastTestWhp', 'pressureAbsolute', 'Last well-test well-head pressure.', { suffix: 'Whp' }),
];

/**
 * The reservoir-analysis-only extras on `WellTestReservoirAnalysis_Agent` (B3)
 * beyond the shared {@link WELLTEST_INDEX_PRESSURE} sub-family, applied with
 * `prefix:'model'` (`modelMinStableRate` → `minStableRate`, …): minimum stable
 * rate, stability flag, analysis status, and the well-test timestamp.
 */
export const WELLTEST_RESERVOIR_EXTRAS: Shape = [
  col('minStableRate', 'liquidRate', 'Minimum stable production rate.', { suffix: 'MinStableRate' }),
  col('stabilityFlag', 'unitless', 'Well stability flag (0/1).', { suffix: 'StabilityFlag' }),
  col('status', 'unitless', 'Reservoir-analysis status.', { suffix: 'Status' }),
  col('timestamp', 'unitless', 'Well-test timestamp.', { suffix: 'WellTestTimestamp' }),
];

/**
 * The `model`-marked timeseries parameter family on
 * `WellTestModelParameters_Agent` (B3), applied with `prefix:'model'` to de-mark
 * the `model` marker (`modelIprC` → `iprC`, …): Prosper/VLP/IPR model
 * parameters, match status, revision number, ESP/gas-lift well-test settings,
 * and each one's percent-change. NOTE the source's two non-timeseries scalars
 * (`hasProsperModel`, `prosperModelExternalId`) are NOT in this group — they are
 * kept bare (`prefix:''`) via {@link WELLTEST_MODEL_SCALARS}.
 */
export const WELLTEST_MODEL_PARAMETERS: Shape = [
  col('espWf', 'unitless', 'ESP wear factor.', { suffix: 'EspWf' }),
  col('espWfPercentChange', 'fraction', 'Percent change in ESP wear factor.', { suffix: 'EspWfPercentChange' }),
  col('glOrificeSize', 'chokeSize', 'Gas-lift orifice size.', { suffix: 'GlOrificeSize' }),
  col('glOrificeSizePercentChange', 'fraction', 'Percent change in gas-lift orifice size.', {
    suffix: 'GlOrificeSizePercentChange',
  }),
  col('iprC', 'unitless', 'IPR C coefficient.', { suffix: 'IprC' }),
  col('iprN', 'unitless', 'IPR N exponent.', { suffix: 'IprN' }),
  col('matchStatus', 'unitless', 'Model match status.', { suffix: 'MatchStatus' }),
  col('reservoirModel', 'unitless', 'Reservoir model identifier.', { suffix: 'ReservoirModel' }),
  col('revisionNumber', 'unitless', 'Model revision number.', { suffix: 'RevisionNumber' }),
  col('uval', 'unitless', 'Model U-value.', { suffix: 'Uval' }),
  col('vlpCorrelation', 'unitless', 'VLP correlation identifier.', { suffix: 'VlpCorrelation' }),
  col('vlpParameter1', 'unitless', 'VLP parameter 1.', { suffix: 'VlpParameter1' }),
  col('vlpParameter1PercentChange', 'fraction', 'Percent change in VLP parameter 1.', {
    suffix: 'VlpParameter1PercentChange',
  }),
  col('vlpParameter2', 'unitless', 'VLP parameter 2.', { suffix: 'VlpParameter2' }),
  col('vlpParameter2PercentChange', 'fraction', 'Percent change in VLP parameter 2.', {
    suffix: 'VlpParameter2PercentChange',
  }),
  col('wellTestEspFrequency', 'frequency', 'Well-test ESP drive frequency.', { suffix: 'WellTestEspFrequency' }),
  col('wellTestGlInjection', 'gasRate', 'Well-test gas-lift injection rate.', { suffix: 'WellTestGlInjection' }),
];

/**
 * The two non-timeseries scalar columns on `WellTestModelParameters_Agent` (B3),
 * kept bare (`suffix === canonical`, `prefix:''`): the Prosper-model presence
 * flag and the Prosper-model external id. Separate from
 * {@link WELLTEST_MODEL_PARAMETERS} because they carry no `model` marker and are
 * scalars, not timeseries.
 */
export const WELLTEST_MODEL_SCALARS: Shape = [
  col('hasProsperModel', 'unitless', 'Whether a Prosper model exists for the string.'),
  col('prosperModelExternalId', 'unitless', 'External id of the associated Prosper model.'),
];

/**
 * The troubleshooting-ticket record family on `TicketRegistry_Agent` (B3): the
 * ticket identity, summary, lifecycle status/priority, dates, assignment,
 * narrative fields, classification, and the opportunity flag. All bare scalars
 * (`suffix === canonical`, `prefix:''`) — no de-prefix axis, no timeseries, no
 * direct relation (the entity association is the scalar string `entityName`).
 * NOTE `isOpportunity` is a STRING flag (`'true'`/`'false'`), not a boolean.
 */
